import moment from "moment";
import taskService from "api/taskService";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import ActionsCalendar from "components/ActionsCalendar";
import { useNavigate, useParams } from "react-router-dom";
import ActionsList from "components/ActionsList";
import { registerLocale } from "react-datepicker";
import pl from "date-fns/locale/pl";
import ArrowIcon from "assets/icons/arrow_ios.svg?react";
import "./calendar.scss";
import MonthList from "components/MonthList";
import { useAuth } from "react-oidc-context";
import { getUserId } from "utils/getUserId";
import { DateCalendar, PickersDay, PickersDayProps } from "@mui/x-date-pickers";
import { Checkbox } from "@mui/material";
import SearchBar from "components/SearchBar";
import Tasks from "components/Tasks";
import ContextMenuCalendar from "components/ContextMenuCalendar";
import { DateRangeCalendar } from "@mui/x-date-pickers-pro";
registerLocale("pl", pl);
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretDown,
  faCaretUp,
} from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { SignalRContext } from "../../App";
import { HubCallbacksNames } from "api/hub";

export function ServerDay(
  props: PickersDayProps<any> & {
    highlightedDays?: { date: Date; count: number }[];
  }
) {
  const { highlightedDays = [], day, outsideCurrentMonth, ...other } = props;

  // Sprawdzamy, czy dany dzień jest aktualny
  const actualDay = moment(new Date()).format("DDMM");
  let calendarData = props.day.date();
  if (calendarData <= 9) {
    calendarData = `0${calendarData}`;
  }

  // Stylizacja dla wybranego dnia
  const isActualDate =
    actualDay === calendarData && !props.selected ? styles.isActualDate : "";
  const selectedStyle2 = props.selected ? styles.selectedDate : "";

  // Pobieramy licznik dla danego dnia (jeśli istnieje)
  const dayCount = highlightedDays.find((d) =>
    moment(d.date).isSame(props.day, "day")
  )?.count;

  return (
    <div className="relative">
      {dayCount !== undefined && dayCount > 0 && (
        <div
          className="absolute right-0 z-10  w-[16px] text-white h-[16px] flex items-center justify-center rounded-full"
          style={{
            background: "#B8006A",
            fontSize: "11px",
            fontWeight: "500",
          }}
        >
          {dayCount}
        </div>
      )}
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
        className={`${isActualDate} ${selectedStyle2}`}
      />
    </div>
  );
}

export interface ISearch {
  text: string;
  statusId: string | null;
  tagId: string | null;
  entityId: string | null;
  kind: number | null;
}

export const CustomHeader = ({
  date,
  decreaseMonth,
  increaseMonth,
  changeYear,
}: {
  date: Date;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  changeYear: any;
}) => {
  const { t } = useTranslation();
  const months = [
    t("calendar.months.january"),
    t("calendar.months.february"),
    t("calendar.months.march"),
    t("calendar.months.april"),
    t("calendar.months.may"),
    t("calendar.months.june"),
    t("calendar.months.july"),
    t("calendar.months.august"),
    t("calendar.months.september"),
    t("calendar.months.october"),
    t("calendar.months.november"),
    t("calendar.months.december"),
  ];
  return (
    <div className={styles.headerWrapper}>
      <div className={styles.monthWrapper}>
        <ArrowIcon onClick={decreaseMonth} className={styles.icon} />
        {months[moment(date).month()]}
        <ArrowIcon
          onClick={increaseMonth}
          className={`${styles.icon} ${styles.reverse}`}
        />
      </div>
      <div className={styles.yearWrapper}>
        {" "}
        <ArrowIcon
          onClick={() => {
            changeYear(moment(date).subtract(1, "year").format("YYYY"));
          }}
          className={styles.icon}
        />
        {moment(date).year()}{" "}
        <ArrowIcon
          onClick={() => {
            changeYear(moment(date).add(1, "year").format("YYYY"));
          }}
          className={`${styles.icon} ${styles.reverse}`}
        />
      </div>
    </div>
  );
};

const ActionsModule = ({
  onlyFromCourtPortal = false,
  header,
}: {
  onlyFromCourtPortal?: boolean;
  header?: string;
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const userId = getUserId(auth.user!);

  const [actualMonthView, setActualMonthView] = useState(
    moment(new Date()).month()
  );

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarDate, setCalendarDate] = useState(new Date());
  const { calendarView } = useParams();
  const navigate = useNavigate();

  const [selectedCalendars, setSelectedCalendars] = useState<string[]>([]);

  const { data: busyEventData, refetch: refetchBusyDates } =
    taskService.useGetBusyForEvents(
      moment(calendarDate).startOf("month").subtract(7, "d").toISOString(),
      moment(calendarDate).endOf("month").add(7, "d").toISOString(),
      selectedCalendars,
      onlyFromCourtPortal
    );

  SignalRContext.useSignalREffect(
    HubCallbacksNames.NewEvent,
    (message) => {
      console.log("Got new event", message);
      refetchBusyDates();
      // notification({
      //   title: `Użytkownik OFFLINE`,
      //   text: `Inny użytkownik OFFLINE: ${message.userId}`,
      //   type: ENotificationType.ERROR,
      // });
    },
    []
  );

  const { data: calendarsData } = taskService.useGetCalendars();

  const busyEventDates = useMemo(() => {
    if (!busyEventData?.data) return [];
    if (!busyEventData?.data.map) return [];

    return (busyEventData?.data ?? []).map(
      (date: { Date: string; Count: number }) => {
        return {
          date: moment(date.Date).toDate(),
          count: date.Count,
        };
      }
    );
  }, [busyEventData]);

  const calendars = useMemo(() => {
    if (!calendarsData?.data) return [];
    if (!calendarsData?.data.map) return [];

    return calendarsData?.data;
  }, [calendarsData, calendarsData?.data]);

  const allBusyDates = Object.entries(
    [...(busyEventData?.data ?? [])].reduce((acc, item) => {
      acc[item.Date] = (acc[item.Date] || 0) + item.Count;
      return acc;
    }, [])
  ).map(([Date, Count]) => ({ date: moment(Date).toDate(), count: Count }));

  const handleClickNavigation = (type: string) => {
    localStorage.setItem(`${userId}.calendar_view`, type);

    if (!onlyFromCourtPortal) {
      navigate(`/actions/${type}`);
    } else {
      navigate(`/courtPortal/calendar/${type}`);
    }
  };

  const handleChangeDate = (date: Date) => {
    setSelectedDate(date);
  };

  useEffect(() => {
    const view = localStorage.getItem(`${userId}.calendar_view`);
    if (view) {
      handleClickNavigation(view);
    } else {
      handleClickNavigation("day");
    }
  }, []);

  const handleChangeCalendarDate = (fromDate: Date, toDate: Date) => {
    if (calendarView !== "month") {
      setCalendarDate(moment(fromDate).subtract(1, "day").toDate());
      return;
    }

    setCalendarDate(moment(fromDate).add(8, "days").toDate());
  };

  useEffect(() => {
    if (calendars) {
      setSelectedCalendars(calendars.map((i) => i.id));
    }
  }, [calendars]);

  const menu = () => {
    return (
      <ContextMenuCalendar
        handleClickNavigation={handleClickNavigation}
        calendarView={calendarView}
        onlyFromCourtPortal={onlyFromCourtPortal}
      />
    );
  };

  const [search, setSearch] = useState<ISearch>({
    text: "",
    statusId: null,
    tagId: null,
    entityId: null,
    kind: null,
  });

  const [isCalendarsExpanded, setIsCalendarsExpanded] = useState<boolean>(true);

  const handleChangeSearchBar = (
    text: string,
    statusId: string | null,
    tagId: string | null,
    entityId: string | null,
    kind: number | null
  ) => {
    setSearch({
      text,
      statusId,
      tagId,
      entityId,
      kind,
    });
  };

  const leftPanel = () => {
    return (
      <div
        className={`${styles.leftWrapper} ${
          calendarView === "tasks" && styles.removeBG
        }`}
      >
        {(calendarView === "day" ||
          calendarView === "week" ||
          calendarView === "month") && (
          <ActionsCalendar
            search={search}
            handleChangeDate={handleChangeCalendarDate}
            date={selectedDate}
            calendars={selectedCalendars}
            onlyFromCourtPortal
          />
        )}
        {calendarView === "list" && (
          <ActionsList search={search} date={selectedDate} listType="events" />
        )}
        {calendarView === "tasks" && <Tasks search={search} />}
      </div>
    );
  };

  const Accordion = ({ expanded, setExpanded, children }) => {
    // By using `AnimatePresence` to mount and unmount the contents, we can animate
    // them in and out while also only rendering the contents of open accordions
    return (
      <>
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.section
              key="content"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { opacity: 1, height: "auto" },
                collapsed: { opacity: 0, height: 0 },
              }}
            >
              {children}
            </motion.section>
          )}
        </AnimatePresence>
      </>
    );
  };

  const calendarsSection = () => {
    return (
      <div className={styles.calendars}>
        <div className={styles.header}>
          {t("calendar.myCalendars")}
          <div
            onClick={(e) => {
              setIsCalendarsExpanded(!isCalendarsExpanded);
              e.stopPropagation();
            }}
            className={styles.expanderWrapper}
          >
            <FontAwesomeIcon
              className={classNames(styles.expander)}
              icon={isCalendarsExpanded ? faCaretDown : faCaretUp}
            />
          </div>
        </div>
        <Accordion
          expanded={isCalendarsExpanded}
          setExpanded={setIsCalendarsExpanded}
        >
          <div className={styles.list}>
            {calendars &&
              calendars.map((c) => (
                <div className={styles.calendar}>
                  <Checkbox
                    checked={!!selectedCalendars.find((x) => x == c.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCalendars([...selectedCalendars, c.id]);
                      } else {
                        setSelectedCalendars(
                          selectedCalendars.filter((x) => x !== c.id)
                        );
                      }
                    }}
                    sx={{
                      color: c.color ?? "#614bc4",
                      "&.Mui-checked": {
                        color: c.color ?? "#614bc4",
                      },
                      padding: "0 9px 0 0 ",
                    }}
                  />
                  {c.name}
                </div>
              ))}
          </div>
        </Accordion>
      </div>
    );
  };

  const rightPanel = () => {
    if (calendarView === "tasks") {
      return <></>;
    }
    // if (calendarView === 'list') return <></>;

    if (calendarView === "month") {
      return (
        <div className={styles.rightWrapper}>
          <MonthList date={calendarDate} handleChangeDate={handleChangeDate} />
          {!onlyFromCourtPortal && calendarsSection()}
        </div>
      );
    }

    if (calendarView === "week") {
      return (
        <div className={styles.rightWrapper}>
          <DateRangeCalendar
            // onAccept={handleAcceptDate}
            // onClose={() => {
            //   setShowCalendar(false);
            // }}
            // slots={{
            //   calendarHeader: (a) => {
            //     console.log(a);
            //     return <></>;
            //   },
            // }}
            showDaysOutsideCurrentMonth
            fixedWeekNumber={6}
            defaultValue={[
              moment(calendarDate),
              moment(calendarDate).add(6, "day"),
            ]}
            value={[moment(calendarDate), moment(calendarDate).add(6, "day")]}
            sx={{
              "Mui-selected": {
                background: "red",
              },
            }}
            onChange={(d) => {
              console.log(d);
              setSelectedDate(d[0]);
            }}
            calendars={1}
          />
          {!onlyFromCourtPortal && calendarsSection()}
        </div>
      );
    }

    return (
      <div className={styles.rightWrapper}>
        <DateCalendar
          value={moment(calendarDate)}
          showDaysOutsideCurrentMonth
          fixedWeekNumber={6}
          defaultValue={moment(calendarDate).utc()}
          onMonthChange={(date) => {
            setActualMonthView(moment(date).month());
            // setCalendarDate(moment(date).toDate() ?? new Date());
          }}
          onChange={(newValue) => {
            // setCalendarDate(moment(newValue).toDate() ?? new Date());
            setSelectedDate(moment(newValue).toDate() ?? new Date());
          }}
          slots={{
            day: ServerDay,
          }}
          slotProps={{
            day: {
              highlightedDays:
                calendarView === "day" ||
                calendarView === "week" ||
                calendarView === "month"
                  ? allBusyDates.filter(
                      (f: { date: Date; count: number }) =>
                        moment(f.date).month() === actualMonthView
                    )
                  : busyEventDates.filter(
                      (f: { date: Date; count: number }) =>
                        moment(f.date).month() === actualMonthView
                    ),
            } as any,
          }}
        />
        {!onlyFromCourtPortal && calendarsSection()}
      </div>
    );
  };

  return (
    <div className={styles.layout}>
      <div className={styles.header}>
        <div className={styles.left}>
          <div className={styles.title}>{header ?? t("actions.title")}</div>
          <div className={styles.subTitle}></div>
        </div>
        <div className={styles.right}>
          <SearchBar
            onChange={(t, statusId, tagId, entityId, kind) =>
              handleChangeSearchBar?.(t, statusId, tagId, entityId, kind)
            }
          />
        </div>
      </div>
      {menu()}
      <div className={styles.list}>
        <div
          className={`${styles.leftY} ${
            calendarView === "tasks" && styles.fullsize
          }`}
        >
          {leftPanel()}
        </div>

        <div
          className={`${styles.rightY} ${
            calendarView === "tasks" && styles.removeMenu
          }`}
        >
          {rightPanel()}
        </div>
      </div>
    </div>
  );
};

export default ActionsModule;
